/**
 * The classes every control is built from, so a button on the overview and
 * a button in the drawer are one button. The looks and heights:
 *
 *   primary    the gradient, with the glow. One per screen.
 *   secondary  the outlined white button: "Open on Greenhouse".
 *   outline    the same on the 3:1 edge, for what is one step from primary.
 *   solid      the text colour as a fill, as a selected chip: a popover's "Apply".
 *   ghost      a hairline and muted text: "Skip", "Previous".
 *   applied    the applied chip as a button, for the queue's rows.
 *
 *   tall  46 px, the actions of the posting card
 *   lg    40 px, the topbar and the page header
 *   page  40 px, on a page that takes the type tokens
 *   md    36 px, a small panel
 *   sm    30 px, a table row
 *
 * On a phone every one is at least 44 px, a thumb's size.
 */

export type ButtonVariant = "primary" | "secondary" | "outline" | "solid" | "ghost" | "applied";
export type ButtonSize = "tall" | "lg" | "page" | "md" | "sm";

const VARIANT: Record<ButtonVariant, string> = {
    primary: "border-0 bg-grad-btn text-brand-fg font-semibold shadow-glow hover:saturate-[1.08]",
    secondary: "border border-line-strong bg-card text-fg font-semibold hover:bg-hover",
    outline: "border border-line-control bg-card text-fg font-semibold hover:bg-hover",
    solid: "border-0 bg-fg text-on-fg font-semibold hover:opacity-90",
    ghost: "border border-line bg-transparent text-fg-muted font-medium hover:bg-hover hover:text-fg",
    applied:
        "border border-[rgba(33,212,92,0.45)] bg-applied text-applied-fg font-semibold hover:saturate-[1.15]",
};

const SIZE: Record<ButtonSize, string> = {
    tall: "h-[46px] gap-2 rounded-control px-4 text-[15px] leading-[22px]",
    lg: "h-10 gap-2 rounded-control px-4 text-[13px]",
    page: "h-10 gap-2 rounded-control px-4 text-ui",
    md: "h-9 gap-1.5 rounded-[10px] px-3.5 text-[12.5px]",
    sm: "h-[30px] gap-1.5 rounded-[9px] px-2.5 text-[12px]",
};

export function buttonClass(
    variant: ButtonVariant = "secondary",
    size: ButtonSize = "md",
    extra = "",
): string {
    return `inline-flex shrink-0 items-center justify-center whitespace-nowrap max-[760px]:min-h-11 max-[760px]:min-w-11 transition-[background-color,filter] duration-150 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent ${VARIANT[variant]} ${SIZE[size]} ${extra}`.trim();
}

/** A filter chip: a pill that is outlined until it is the one selected. */
export function chipClass(selected: boolean, extra = ""): string {
    return `inline-flex h-[30px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[12px] max-[760px]:min-h-11 transition-colors ${
        selected
            ? "border-fg bg-fg font-semibold text-on-fg"
            : "border-line bg-transparent font-medium text-fg hover:border-line-strong"
    } ${extra}`.trim();
}

/** A filter's dropdown button: the green tint once a value is picked. */
export function filterButtonClass(selected: boolean): string {
    return `inline-flex h-10 min-w-0 max-w-full items-center gap-2 rounded-lg border px-3 text-[13px] font-medium whitespace-nowrap transition-colors focus-visible:ring-focus max-[760px]:min-h-11 ${
        selected
            ? "border-pay-line bg-tint-green text-brand-ink"
            : "border-line bg-card text-fg hover:border-line-strong"
    }`;
}

/** The 10.5 px uppercase label over a group of things. */
export const LABEL_CLASS = "text-[10.5px] font-semibold uppercase tracking-[0.08em] text-fg-faint";
